<template>
  <b-modal ref="modal" size="xlg" lazy class="modal-untitled" :hide-header="true" :hide-footer="true" :title="getTitle()"
    @hide="onHide">
    <template v-slot:modal-title>
      <b-alert show variant="light">
        <strong class="font100">
          {{ getTitle() }}
        </strong>
        <div v-if="order.label.tracking_id" style="font-size: 12px;">
          <a href="javascript:void(0);" :id="`popover-${order.label.tracking_id}-modal`">TRACKING #: {{
            order.label.tracking_id }}</a>
          <b-popover :target="`popover-${order.label.tracking_id}-modal`" triggers="click blur" placement="right">
            <b-row class="flex-column">
              <b-col>
                <a :href="`https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.label.tracking_id}`"
                  target="_blank">Open Link</a>
              </b-col>
              <b-col>
                <a href="javascript:void(0);" @click="copyTrackingID(order.label.tracking_id)">Copy</a>
              </b-col>
            </b-row>
          </b-popover>
        </div>
        <div v-if="order.ref_id" style="font-size: 12px;">
          REFERENCE ID: {{ order.ref_id }}
        </div>
        <button @click="hide" type="button" aria-label="Close" class="close">×</button>
      </b-alert>
    </template>

    <div class="order-details--modal">
      <LoadingSpinner v-if="isLoading" />
      <Alerts :key="'alerts' + order.id" />
      <b-row>
        <b-col sm="12" xl="9">
          <b-row>
            <b-col sm="12" xl="8">
              <div class="card">
                <div class="card-header">Order Summary</div>
                <div class="card-body">
                  <table class="table borderless">
                    <tbody>
                      <tr>
                        <th v-if="order.created_at">Order Date</th>
                        <td v-if="order.created_at">{{ formatDate(order.created_at) }}</td>
                        <th>Currency</th>
                        <td>{{ order.metadata.currency }}</td>
                      </tr>
                      <tr>
                        <th v-if="order.metadata.processed_at">Paid Date</th>
                        <td v-if="order.metadata.processed_at">{{ formatDate(order.metadata.processed_at) }}</td>
                        <th>Product Total</th>
                        <td>{{ formatPrice(order.metadata.subtotal_price) }}</td>
                      </tr>
                      <tr>
                        <th>Hold Until</th>
                        <td>{{ order.metadata.hold }}</td>
                        <th>Shipping Paid</th>
                        <td>
                          <span v-if="order.shipping_lines.length">{{ getShippingPaid(order.shipping_lines, 1) }}</span>
                        </td>
                      </tr>
                      <tr>
                        <th>Ship By</th>
                        <td>{{ order.metadata.ship_by }}</td>
                        <th>Tax Paid</th>
                        <td>{{ formatPrice(order.metadata.total_tax) }}</td>
                      </tr>
                      <tr>
                        <td></td>
                        <td></td>
                        <th class="border-top">Total Order</th>
                        <td class="border-top">{{ formatPrice(order.total_price) }}</td>
                      </tr>
                      <tr v-if="order.remote_id && order.marketplace.code !== 'grandshipper'">
                        <th>Marketplace ID</th>
                        <td colspan="3">
                          <a :href="order.remote_url" target="_blank">
                            {{ order.order_number }}&nbsp;&nbsp;&nbsp;
                            <span class="fa fa-external-link"></span>
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="card">
                <div class="card-header">Items Ordered</div>
                <div class="card-body">
                  <OrderItems :order="order" />
                </div>
              </div>
            </b-col>
            <b-col sm="12" xl="4">
              <b-card>
                <div slot="header">Ship From</div>
                {{ shipmentOrigin }}
              </b-card>
              <b-card>
                <div slot="header">Shipping Address</div>
                <b-card-body>
                  <p class="card-text">
                    <strong v-if="order.shipping_address.first_name || order.shipping_address.last_name">{{
                      order.shipping_address.first_name || '' }} {{ order.shipping_address.last_name || '' }}
                      <br></strong>
                    <span v-if="order.shipping_address.company">
                      {{ order.shipping_address.company }}
                      <br>
                    </span>
                    {{ order.shipping_address.address }}
                    <span v-if="order.shipping_address.address2">{{ order.shipping_address.address2 }}</span>
                    <br>
                    {{ order.shipping_address.city }}, {{ order.shipping_address.province }} {{
                      order.shipping_address.postal_code }}
                    <br>
                    {{ getCountryName(order.shipping_address.country) }}
                  </p>
                  <p class="card-text" v-if="order.shipping_address.phone">Phone: {{ order.shipping_address.phone }}</p>
                  <p class="card-text" v-if="order.shipping_address.email">Email: {{ order.shipping_address.email }}</p>
                </b-card-body>
              </b-card>
              <b-card body-class="table-green" :no-body="true">
                <div slot="header">Package Details</div>
                <table class="table table-green borderless shipping-info mb-0">
                  <tbody>
                    <tr>
                      <th>Service</th>
                      <td>
                        {{ serviceOptions }}
                      </td>
                    </tr>
                    <tr>
                      <th>Package</th>
                      <td>
                        {{ packageOptions }}
                      </td>
                    </tr>
                    <tr>
                      <th>Weight</th>
                      <td>
                        {{ order.total_weight ? getWeight(order.total_weight).lb : 0.0 }} lb
                        &amp;
                        {{ order.total_weight ? getWeight(order.total_weight).oz : 0.0 }} oz
                      </td>
                    </tr>
                    <tr>
                      <th>Dimensions</th>
                      <td>
                        {{ order.package_length }}x{{ order.package_width }}x{{ order.package_height }}in
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col sm="12" class="d-none">
              <div class="card order-activity">
                <div class="card-header">Order Activity</div>
                <div class="card-body">
                  <ul class="list-group">
                    <li href="#" class="list-group-item list-group-item-action flex-column align-items-start">
                      <div class="d-flex w-100 justify-content-between">
                        <h5 class="mb-1">Import</h5>
                        <small class="text-muted">{{ formatDate(order.created_at) }}</small>
                      </div>
                      <p class="mb-1">Order {{ order.order_number }} imported from {{ order.marketplace.name }}</p>
                    </li>

                    <li href="#" class="list-group-item list-group-item-action flex-column align-items-start">
                      <div class="d-flex w-100 justify-content-between">
                        <h5 class="mb-1">Import</h5>
                        <small class="text-muted">{{ formatDate(order.created_at) }}</small>
                      </div>
                      <p class="mb-1">Order {{ order.order_number }} imported from {{ order.marketplace.name }}</p>
                    </li>
                    <li href="#" class="list-group-item list-group-item-action flex-column align-items-start">
                      <div class="d-flex w-100 justify-content-between">
                        <h5 class="mb-1">Import</h5>
                        <small class="text-muted">{{ formatDate(order.created_at) }}</small>
                      </div>
                      <p class="mb-1">Order {{ order.order_number }} imported from {{ order.marketplace.name }}</p>
                    </li>
                  </ul>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-col>

        <b-col sm="12" xl="3">
          <div class="card">
            <div class="card-header">Additional Services</div>
            <table class="table borderless shipping-info mb-0">
              <tbody>
                <tr>
                  <th>Tracking</th>
                  <td>
                    {{ signatureOptions }}
                  </td>
                </tr>
                <tr>
                  <th>Insurance</th>
                  <td>
                    {{ insuranceOptions }}
                    <!-- <span v-if="order.shipsurance_id">Shipsurance</span> -->
                  </td>
                </tr>
                <tr v-if="order.declared_value">
                  <th>
                    Declared Value
                  </th>
                  <td>
                    {{ order.declared_value }}
                  </td>
                </tr>
                <tr v-if="order.future_shipping_date">
                  <th>Postmark Date</th>
                  <td>
                    {{ order.future_shipping_date | utcDate("MMM DD YYYY") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-card :no-body="true">
            <div slot="header">Postage Summary</div>
            <table class="table borderless shipping-info mb-0">
              <tbody>
                <tr v-if="order.rate">
                  <th class="table-green">Shipping Rate:</th>
                  <td>{{ formatPrice(order.rate) }}</td>
                </tr>
                <tr v-if="parseInt(order.surcharge)">
                  <th class="table-green">Surcharge:</th>
                  <td>
                    <span style="color: #dc3545;" v-if="surcharge_msg">{{ surcharge_msg }} <br></span>
                    {{ formatPrice(order.surcharge) }}
                  </td>
                </tr>
                <tr v-for="rateComponent in rateComponents" :key="rateComponent.specialServiceId">
                  <th class="">{{ rateComponentName(rateComponent) }}:</th>
                  <td class="">{{ rateComponentPrice(rateComponent) }}</td>
                </tr>
                <tr v-if="order.order_status_id !== 5">
                  <th class>Estimated Delivery Date:</th>
                  <td> {{ deliveryEstimate }} </td>
                </tr>
                <tr v-if="order.rate">
                  <th class="bg-light">
                    <em v-if="order.label.return_status === 'printed' && order.order_status_id === 5">
                      Estimated Total Cost:
                    </em>
                    <span v-else>
                      Total Cost:
                    </span>
                  </th>
                  <td class="bg-light">
                    <em v-if="order.label.return_status === 'printed' && order.order_status_id === 5">
                      {{ formatPrice(order.total_price) }}
                    </em>
                    <span v-else>
                      {{ formatPrice(order.total_price) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-card>
          <b-card>
            <div slot="header">Printed By</div>
            {{ order.printed_by }}
          </b-card>
          <b-card class="buttons" :no-body="true" v-if="order.order_status_id !== 4">
            <div slot="header">Shipping Options</div>
            <b-card-footer>
              <b-btn-group vertical>
                <b-btn v-if="order.order_status_id !== 5" class="mb-2 btn-grad" variant="success"
                  :href="`#/orders/new-shipment?id=${order.id}`" size="sm">
                  Replicate Shipment
                </b-btn>
                <b-btn class="mb-2 btn-grad" variant="success" @click="printLabel('return')" size="sm">
                  <span v-if="order.order_status_id !== 5">Print</span>
                  <span v-else>Reprint</span>
                  Scan Based Return Label
                </b-btn>
                <b-dropdown ref="expiredDropdown" v-if="isExpired" variant="link" toggle-class="text-decoration-none"
                  dropup class="w-100 dPrint">
                  <template slot="button-content">
                    Reprint Label
                  </template>
                  <b-dropdown-text class="m-0 py-1">
                    Sorry, the postmark for this label has passed 90 days. Reprint function is unavailable.
                  </b-dropdown-text>
                </b-dropdown>
                <b-btn v-else-if="order.order_status_id !== 5" :disabled="isExpired"
                  :class="['mb-2', 'btn-grad', 'w-100',]" variant="success" size="sm" @click="printLabel('pdf')">
                  Reprint Label
                </b-btn>
                <b-btn v-if="order.order_status_id !== 5 && order.insurance_price && !order.shipsurance_claim"
                  class="mb-2 btn-grad" variant="primary" size="sm" @click="claimShipsurance">
                  File Shipsurance Claim
                </b-btn>
                <b-btn v-else-if="order.order_status_id !== 5 && order.shipsurance_claim" class="mb-2 btn-grad"
                  variant="primary" size="sm" @click="checkClaimStatus">
                  Check Claim Status
                </b-btn>
                <!-- <b-btn
                  v-if="!featureLocked('packing-slips')"
                  class="mb-2 btn-grad"
                  variant="success"
                  @click="e => printPackingSlip()"
                >Print Packing Slip</b-btn> -->
              </b-btn-group>
            </b-card-footer>
            <!-- <a href="#" @click="showOrders">CLICK TO SHOW ORDERS ME</a> -->
          </b-card>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<style lang="scss">
.gray-btn {
  background-color: #d1cfcf !important;
  border: 1px solid #d1cfcf !important;
  color: #000 !important;
  font-weight: bold !important;
}

.dPrint {
  .btn {
    opacity: 0.65;
    background-color: #343a40;
    border-color: #343a40;
    border-radius: 3px;
    color: white;

    &:hover,
    &:active,
    &:focus {
      color: white;
    }
  }

  .dropdown-toggle {

    &::before,
    &::after {
      display: none !important;
    }
  }

  .dropdown-menu {
    display: none;
  }
}
</style>

<style lang="scss" scoped>
.card-wrap {
  //display: flex;
}

.card {
  margin-bottom: 15px;
  font-size: 0.75rem;
}

.card-header {
  background-color: #1e88e5;
  color: #fff;
  font-size: 0.95rem !important;
  padding: 0.10rem 1rem;

  * {
    color: #fff;
  }
}

.borderless {

  td,
  th,
  .list-group-item {
    border: none;
  }
}

.order-activity {
  .list-group {
    .list-group-item {
      border-left: none;
      border-right: none;

      &:first-child {
        border-top: none;
      }

      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.btn-group-vertical {
  width: 100%;

  .btn {
    border-radius: 18px !important;
  }
}
</style>
<script>
import LoadingSpinner from "../../components/LoadingSpinner";
import OrderItems from "../../components/Orders/OrderItems";
import numeral from "numeral";
import Alerts from "../../components/Alerts";
import { mapGetters, mapActions } from "vuex";
import formatters from "../../formatters";
import countries from "../../data/countries";
import moment from "moment";
import sorting from "../../data/sorting";
import Security from "../../mixins/Security";
import DatePicker from "vue-bootstrap-datetimepicker";
import hash from "object-hash";

export default {
  components: {
    LoadingSpinner,
    OrderItems,
    Alerts,
    DatePicker
  },
  mixins: [
    Security
  ],
  props: ["orderobj"],
  data() {
    return {
      isLoading: false,
      order: this.orderobj,
      rules: null,

      shippingServices: [],
      specialServices: {},
      selectedInsurance: {},
      selectedInsuranceInputs: {},
      surcharge_msg: null,

      getTitle() {
        let title = this.order.marketplace.name + " Order "
        if (this.order.order_number) {
          title = title + "#" + this.order.order_number
        }
        return title.toUpperCase()
      },
      getMeta: function (key) {
        if (this.order.metadata.hasOwnProperty(key)) {
          //if( typeof this.order.metadata[key] !== 'undefined' ){
          return this.order.metadata[key];
        }
        return "";
      },
      getShippingPaid: function (shipping_lines, format = false) {
        let total = 0;
        for (let line of shipping_lines) {
          total += parseFloat(line.amount);
        }

        if (format) {
          return this.formatPrice(total);
        }

        return total;
      },
      formatDate: function (date) {
        return date ? formatters.date(date) : "Unknown";
      }
    };
  },
  computed: {
    ...mapGetters({
      getSignatureCarrierRules: "getSignatureCarrierRules",
      getInsuranceCarrierRules: "getInsuranceCarrierRules",
      getServicePackageRules: "getServicePackageRules",
      getUserOrigins: "getUserOrigins",
      user: "getUser",
      employee_name: "getEmployeeName",
      getCarrierRules: "getCarrierRules"
    }),
    deliveryEstimate() {
      if (!(this.order.estimated_delivery_date || this.order.delivery_zone)) {
        return 'N/A';
      } else {
        const delivery_date = this.order.estimated_delivery_date ? moment(this.order.estimated_delivery_date).format('ddd, MMM D') : 'N/A';
        return `${this.order.delivery_zone ? `Zone ${this.order.delivery_zone} - ` : ''}${delivery_date}`;
      }
    },
    serviceOptions() {
      try {
        // const id = this.order.shipping_service_id;
        const code = this.order.shipping_service.code;
        const rec = Object.keys(this.carrierRules).find(serviceCode => {
          console.log("service code: ", serviceCode);
          console.log("carrier rules obj: ", this.carrierRules[serviceCode])
          console.log("carrier rules service: ", this.carrierRules[serviceCode].service)
          const ruleId = this.carrierRules[serviceCode].serviceId;
          console.log("code: ", code)
          console.log("RULE ID: ", ruleId)
          return code === ruleId;
        });
        if (!rec) return "N/A";
        return this.carrierRules[rec].brandedName;
      } catch (e) {
        console.error('service option error', e);
        return "N/A";
      }
    },
    packageOptions() {
      try {
        const pType = this.order.package_type
        const rules = this.getServicePackageRules(
          this.order.shipping_service.code,
          this.order.shipping_address.country
        );
        const rec = rules.find(rule => {
          return rule.parcelType === pType;
        });
        if (!rec) return "N/A";
        return rec.brandedName;
      } catch (e) {
        console.error('package option error', e);
        return "N/A";
      }
    },

    signatureOptions() {
      try {
        const rules = this.getSignatureCarrierRules(
          this.order.shipping_service.code,
          this.order.package_type,
          this.order.shipping_address.country
        );
        // console.log('signature', rules);

        const rec = rules.find(rule => {
          return this.order.confirmation === rule.specialServiceId;
        });
        if (!rec) return "N/A";
        return rec.brandedName;
      } catch (e) {
        console.error('signature/tracking option error', e);
        return "N/A";
      }
    },
    insuranceOptions() {
      try {
        const rules = this.getInsuranceCarrierRules(
          this.order.shipping_service.code,
          this.order.package_type,
          this.order.shipping_address.country
        );
        // console.log('insurance', rules);
        // console.log(this.order.insurance);

        const rec = rules.find(rule => {
          return this.order.insurance === rule.specialServiceId;
        });
        if (!rec) return "N/A";
        return rec.brandedName;
      } catch (e) {
        console.error('insurance option error', e);
        return "N/A";
      }
    },
    specialServiceName() {
      const service = _.find(this.order.special_services);
    },
    shipmentOrigin() {
      const company = this.order.origin.company;
      let name = `${this.order.origin.first_name || ''} ${this.order.origin.last_name || ''}`;
      if (company && name.trim()) name = `${name}, ${company}`;
      else if (!name.trim()) name = company;
      return name;
    },

    country() {
      let country = "US";

      if (this.order.shipping_address && this.order.shipping_address.country) {
        country = this.order.shipping_address.country;
      }

      return country;
    },
    carrierRules: function () {
      return this.getCarrierRules(this.country || "US");
    },
    confirmation() {
      if (_.isEmpty(this.order.shipping_service)) {
        return null;
      }
      const rules = this.$store.getters.getSignatureCarrierRules(
        this.order.shipping_service.code
      );

      const ruleOptions = rules.map(rule => {
        return {
          value: rule,
          label: rule.brandedName
        };
      });

      return ruleOptions;
    },
    formatted_rate() {
      return this.order.rate ? numeral(this.order.rate).format("$0,0.00") : "$0.00";
    },
    isExpired() {
      if (moment().diff(this.order.created_at, "months", true) > 3) {
        return true;
      }
      return false;
    },
    rateComponents() {
      return this.order.rate_components.filter(r => !['Unknown', 'NonStandardDim1', 'NonStandardDim2', 'NonStandardVol'].includes(r.name))
    },
  },
  methods: {
    ...mapActions(["addPrintJob"]),
    // just added to see ffields
    // showOrders(){
    //   console.log(JSON.stringify(this.order));
    // },
    moment: moment,
    numeral: numeral,
    refresh() { },
    show() {
      this.$refs.modal.show();
      console.log(this.$refs);
    },
    hide() {
      this.$refs.modal.hide();
    },
    rateComponentName(rateObj) {
      const sCode = this.order.shipping_service.code;
      switch (sCode) {
        // Priority Mail Express
        case "EM":
          const rateType = rateObj.type;
          if (rateType === "" || rateType === "others") return "Overnight Service";
          return rateObj.name
          break;
        default:
          return rateObj.name
      }
    },
    rateComponentPrice(rateObj) {
      const sCode = this.order.shipping_service.code;
      // console.log('code', sCode);
      let price = 0;
      switch (sCode) {
        // Priority Mail Express
        case "EM":
          const rateType = rateObj.type;
          // for adult signature and delivery confirmation uses "sig" as a type
          if (rateObj.name === "Signature") return "Included";
          else if (rateType === "" || rateType === "others") return "Yes";
          else price = rateObj.value
          break;
        default:
          price = rateObj.value;
      }

      if (price > 0) return this.formatPrice(price);
      else return "None"
    },
    formatPrice: formatters.formatPrice,
    getCountryName: countries.getName,
    getSpecialServiceName(type) {
      const service = this.order.rate_components.find(comp => {
        return comp.type === type;
      });
      return service ? service.name : null;
    },
    async printLabel(type = null) {
      if (type !== "return") {
        try {
          const msg = "Are you sure you want to reprint this label?"

          if (!Cookies.get(hash(msg))) {
            let dialog = await this.$dialog.confirm({ title: 'Reprint Label', body: msg }, {
              remember: false,
              view: 'print',
              customClass: 'print-dialog',
              proceed: 'Reprint'
            });
            dialog.close();
          }
        } catch (e) {
          return;
        }
      }

      if (type === "return") {
        try {
          const msg = "The Ship and From address will be inverted. &nbsp;Your account will only be charged if the label is used. &nbsp;You can manage this label in the SB Return Label page."

          if (!Cookies.get(hash(msg))) {
            let dialog = await this.$dialog.confirm({ title: 'Scan Based Return Label', body: msg }, {
              remember: true,
              view: 'print',
              customClass: 'print-dialog',
              proceed: 'Print'
            });
            dialog.close();
          }
        } catch (e) {
          return;
        }
      }

      let url = "printlabel/" + this.order.id;
      if (type === "return") {
        url += "/return";
      }
      console.log('print url', url);
      axios
        .post(`${url}?format=b64`, { extra: { employee_name: this.employee_name } })
        .then(response => {
          // this.shipper_addresses = response.data;
          this.isLoading = false;
          //this.order.label_print = response.data.documents[0].contents;

          // Add to printer
          const decoded = {
            label: response.data.label,
            label_url: response.data.label_url,
            type: type
          };
          //const decoded = atob(response.data.label)
          this.addPrintJob(decoded);
          //window.open(response.data.label);
        })
        .catch(error => {
          console.log("nooo", error);
          this.isLoading = false;
          let message = error.response.data.message;
          if (error.response.data.errors) {
            message += ": ";
            message += Object.values(error.response.data.errors).join(" ");
          }
          this.$emit("alert", { message: message, type: "danger" });
          // alert(JSON.parse(error.response.data.message)[0].additionalInfo);
        });
    },
    printPackingSlip() {
      this.isLoading = true;
      let url = `orders/${this.order.id}/packing_slip?format=b64`;

      axios
        .get(url)
        .then(response => {
          this.isLoading = false;

          // Add to printer
          const decoded = response.data;
          this.addPrintJob({
            label: decoded,
            label_url: `orders/${this.order.id}/packing_slip`,
            type: "pdf"
          });
        })
        .catch(error => {
          let message = error.response.data.message;
          this.$emit("alert", { message: message, type: "danger" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    isRateable() {
      return true;
    },
    update() { },
    updateOrder() {
      let vm = this;

      if (this.orderStatusId === 3) {
        return;
      }

      if (this.order) {
        vm.isLoading = true;
        let data = this.order;
        axios
          .patch("/orders/" + this.order.id, data)
          .then(response => {
            /*
            vm.$refs.modal.$emit('alert', {
              message: response.data.message,
              type: 'success'
            });*/

            if (response.data.order) {
              this.order = response.data.order;
            }
            if (response.data.rules) {
              this.rules = response.data.rules;
            }
          })
          .catch(error => {
            console.log("errrr", error);
            vm.$refs.modal.$emit("alert", {
              message: Object.values(error.response.data.errors).join(", "),
              type: "danger"
            });
          })
          .finally(() => {
            //vm.fetchOrders();
            vm.refresh();
            vm.isLoading = false;
          });
      }
    },
    onHide() {
      console.log('hide');
      this.$emit("hide");
    },
    onClickSaveAddress(e) {
      let vm = this;
      vm.isLoading = true;
      axios
        .post("/orders/" + this.order.id + "/address/save", {})
        .then(response => {
          vm.$refs.modal.$emit("alert", {
            message: response.data.message,
            type: "success"
          });

          vm.order.shipping_address.saved = 1;
        })
        .catch(error => {
          vm.$refs.modal.$emit("alert", {
            message: error.response.data.message,
            type: "danger"
          });
        })
        .finally(() => {
          vm.refresh();
          vm.isLoading = false;
        });
    },
    onClickVerifyAddress(e) {
      let vm = this;
      vm.isLoading = true;
      axios
        .post("/orders/" + this.order.id + "/address/verify", {})
        .then(response => {
          vm.$refs.modal.$emit("alert", {
            message: response.data.message,
            type: "success"
          });

          vm.order.shipping_address.saved = 1;
        })
        .catch(error => {
          vm.$refs.modal.$emit("alert", {
            message: error.response.data.message,
            type: "danger"
          });
        })
        .finally(() => {
          vm.refresh();
          vm.isLoading = false;
        });
    },
    getWeight(weight) {
      return {
        lb: Math.floor(weight / 16),
        oz: Math.round(weight % 16)
      };
    },
    onChangeInsurance(value) {
      this.selectedInsuranceInputs = {};

      setTimeout(() => {
        this.update();
      }, 100);
    },
    async claimShipsurance() {
      const base_url = process.env.MIX_SHIPSURANCE_URL
      window.open(`${base_url}/extclaims?cid=grandshipper&shipmentID=${this.order.shipsurance_id}&psid=${this.order.user_id}`);
      this.hide()
      this.$emit("hide");
      this.$emit("refresh");
    },
    async checkClaimStatus() {
      const base_url = process.env.MIX_SHIPSURANCE_URL
      window.open(`${base_url}/claimstatus?cid=grandshipper&claimId=${this.order.shipsurance_claim}`);
      this.hide()
      this.$emit("hide");
      this.$emit("refresh");
    },
    copyTrackingID(id) {
      this.$root.$emit('bv::hide::popover', `popover-${id}-modal`)
      this.copyToClipboard(id)
      this.$emit('alert', {
        message: 'Tracking Number successfully copied to clipboard',
        type: 'success'
      })
    },
    copyToClipboard(textToCopy) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(textToCopy);
      } else {
        let textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        let node = document.getElementById(`popover-${textToCopy}-modal`);
        node.parentNode.insertBefore(textArea, node);
        // document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        return new Promise((res, rej) => {
          document.execCommand('copy') ? res() : rej();
          textArea.remove();
        });
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/views/Orders/ShowShippedInModal.vue